import { PreviewStatus } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/Preview";
import { TFunction } from "i18next";
import { FC, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { ExpandableInfoMessage } from "shared/components/feedback/InfoMessage/ExpandableInfoMessage/ExpandableInfoMessage";
import { Box, Text } from "shared/components/Spor";
import { useTheme } from "styled-components";

type PreviewExample = {
  context: string | null;
  title: string | null;
  message: string | null;
};

type PreviewExampleProps = {
  previewExample: PreviewExample[] | undefined;
  previewStatus: PreviewStatus;
  source: "INTERNAL" | "EXTERNAL";
};

const subtitleFromProps = (
  previewExample: PreviewExample | undefined,
  previewStatus: PreviewStatus,
  translate: TFunction<"translation", undefined>,
): ReactElement | string | undefined => {
  const { title, message } = previewExample ?? {};
  switch (previewStatus) {
    case "idle":
      return translate("Fyll ut obligatoriske felter for å se informasjonen.");
    case "pending":
      return undefined;
    case "success":
      return title ? (
        <>
          <b style={{ display: "block" }}>{title}</b>
          {message}
        </>
      ) : (
        (message ?? translate("Kunne ikke finne distribusjonstekst"))
      );
    case "error":
      return translate("Kunne ikke hente oppsummering.");
  }
};

export const PreviewExample: FC<PreviewExampleProps> = ({
  previewExample = [],
  previewStatus,
  source,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isInternal = source === "INTERNAL";
  const placeholderSubtitle = subtitleFromProps(
    previewExample[0],
    previewStatus,
    t,
  );
  const subtitles = previewExample.map((it) =>
    subtitleFromProps(it, previewStatus, t),
  );

  // Assuming all texts are from the same context
  const context = previewExample[0]?.context;
  return (
    <Box data-testid="previewMessage" data-status={previewStatus}>
      <ExpandableInfoMessage
        title={
          isInternal
            ? t("Eksempel: Interne kanaler")
            : t("Eksempel: Eksterne kanaler")
        }
        semantics="info"
        // Subtitle is passed as a prop and as a child
        // as the prop is shown truncated and the child is shown fully
        hideIcon
        subTitle={placeholderSubtitle}
        skeleton={!placeholderSubtitle && previewStatus === "pending"}
        showExpand={previewStatus === "success"}
        counter={previewExample?.length ?? 0}
        openPaddingY="0px" // Because subtitle ~= children (visually), we dont want it to suddenly add a padding when open
      >
        {subtitles.map((subtitle, index) => (
          <Text
            // eslint-disable-next-line react/no-array-index-key -- No better alternative
            key={index}
            whiteSpace="pre-wrap"
            variant="sm"
            fontWeight="normal"
          >
            {subtitle}
          </Text>
        ))}
        <Text
          variant="sm"
          fontStyle="italic"
          color={theme.colorInfoTextSecondary}
          mt="12px"
        >
          {context ? t("(Kilde: {{source}})", { source: context }) : null}
        </Text>
      </ExpandableInfoMessage>
    </Box>
  );
};
